import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { providerAPI, pingAPI } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { LogOut, Bell, Edit3, Save } from 'lucide-react';
import { toast } from 'sonner';

const ProviderDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [pings, setPings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    clinic_name: '',
    address: '',
    city: '',
    experience: 0,
    specializations: [],
    bio: '',
    languages: [],
    session_type: 'In-person'
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const allSpecializations = ['Anxiety', 'Depression', 'Trauma', 'Relationship', 'Addiction', 'Career'];
  const commonLanguages = ['English', 'Hindi', 'Tamil', 'Telugu', 'Marathi', 'Bengali', 'Gujarati'];

  useEffect(() => {
    fetchProfile();
    fetchPings();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await providerAPI.getProfile(user.id);
      setProfile(response.data);
      setFormData({
        clinic_name: response.data.clinic_name || '',
        address: response.data.address,
        city: response.data.city,
        experience: response.data.experience,
        specializations: response.data.specializations,
        bio: response.data.bio,
        languages: response.data.languages,
        session_type: response.data.session_type
      });
    } catch (error) {
      if (error.response?.status === 404) {
        setEditMode(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPings = async () => {
    try {
      const response = await pingAPI.getProviderPings(user.id);
      setPings(response.data);
    } catch (error) {
      console.error('Failed to fetch pings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.specializations.length === 0) {
      toast.error('Please select at least one specialization');
      return;
    }
    
    if (formData.languages.length === 0) {
      toast.error('Please select at least one language');
      return;
    }

    try {
      if (profile) {
        await providerAPI.updateProfile(user.id, formData);
        toast.success('Profile updated successfully');
      } else {
        await providerAPI.createProfile(formData);
        toast.success('Profile created successfully');
      }
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const toggleLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-[#4B5563]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="font-heading text-2xl font-bold text-primary">Provider Dashboard</h1>
            <div className="flex items-center gap-4">
              <span className="text-[#4B5563]">Welcome, {user?.name}</span>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="text-[#4B5563] hover:text-primary hover:bg-secondary/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Stats Card */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-primary to-primary-hover rounded-2xl p-6 text-white mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Bell className="w-6 h-6" />
                  <h2 className="font-heading text-xl font-semibold">Interest Pings</h2>
                </div>
                <div className="text-5xl font-heading font-bold mb-2" data-testid="ping-count">{pings.length}</div>
                <p className="text-white/90 text-sm">People interested in your services</p>
              </div>

              {/* Pings List */}
              <div className="bg-white rounded-2xl border border-border/40 p-6">
                <h3 className="font-heading text-lg font-semibold text-[#2C3333] mb-4">Recent Pings</h3>
                {pings.length === 0 ? (
                  <p className="text-[#9CA3AF] text-sm" data-testid="no-pings">No interest pings yet</p>
                ) : (
                  <div className="space-y-3">
                    {pings.map((ping, index) => (
                      <div
                        key={ping.ping.id}
                        data-testid={`ping-item-${index}`}
                        className="bg-secondary/50 rounded-lg p-3 border border-border/20"
                      >
                        <p className="font-medium text-[#2C3333]">{ping.seeker.name}</p>
                        <p className="text-sm text-[#4B5563]">{ping.seeker.phone}</p>
                        <p className="text-xs text-[#9CA3AF] mt-1">
                          {new Date(ping.ping.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Profile Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-border/40 p-6 md:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-2xl font-semibold text-[#2C3333]">
                    {profile ? 'Your Profile' : 'Create Your Profile'}
                  </h2>
                  {profile && !editMode && (
                    <Button
                      data-testid="edit-profile-btn"
                      onClick={() => setEditMode(true)}
                      className="bg-primary hover:bg-primary-hover text-white rounded-full"
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                </div>

                {!editMode && profile ? (
                  <div className="space-y-6" data-testid="profile-view">
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Full Name</Label>
                      <p className="text-[#4B5563] mt-1">{user.name}</p>
                    </div>
                    {formData.clinic_name && (
                      <div>
                        <Label className="text-[#2C3333] font-semibold">Clinic/Practice Name</Label>
                        <p className="text-[#4B5563] mt-1">{formData.clinic_name}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Address</Label>
                      <p className="text-[#4B5563] mt-1">{formData.address}</p>
                    </div>
                    <div>
                      <Label className="text-[#2C3333] font-semibold">City</Label>
                      <p className="text-[#4B5563] mt-1">{formData.city}</p>
                    </div>
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Years of Experience</Label>
                      <p className="text-[#4B5563] mt-1">{formData.experience} years</p>
                    </div>
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Specializations</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.specializations.map(spec => (
                          <span key={spec} className="bg-secondary text-primary px-3 py-1 rounded-full text-sm font-medium">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Bio</Label>
                      <p className="text-[#4B5563] mt-1 leading-relaxed">{formData.bio}</p>
                    </div>
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Languages</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.languages.map(lang => (
                          <span key={lang} className="bg-[#F9FAFB] text-[#2C3333] px-3 py-1 rounded-lg text-sm font-medium">
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#2C3333] font-semibold">Session Type</Label>
                      <p className="text-[#4B5563] mt-1">{formData.session_type}</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="profile-form">
                    <div>
                      <Label htmlFor="clinic_name" className="text-[#2C3333]">Clinic/Practice Name (Optional)</Label>
                      <Input
                        id="clinic_name"
                        data-testid="clinic-name-input"
                        value={formData.clinic_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, clinic_name: e.target.value }))}
                        className="mt-2 rounded-lg border-border/60 bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary h-12"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-[#2C3333]">City *</Label>
                        <Input
                          id="city"
                          data-testid="city-input"
                          value={formData.city}
                          onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                          className="mt-2 rounded-lg border-border/60 bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary h-12"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="experience" className="text-[#2C3333]">Years of Experience *</Label>
                        <Input
                          id="experience"
                          data-testid="experience-input"
                          type="number"
                          min="0"
                          value={formData.experience}
                          onChange={(e) => setFormData(prev => ({ ...prev, experience: parseInt(e.target.value) }))}
                          className="mt-2 rounded-lg border-border/60 bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary h-12"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-[#2C3333]">Full Address *</Label>
                      <Input
                        id="address"
                        data-testid="address-input"
                        value={formData.address}
                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        className="mt-2 rounded-lg border-border/60 bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary h-12"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-[#2C3333] mb-3 block">Areas of Specialization *</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="specializations">
                        {allSpecializations.map(spec => (
                          <div key={spec} className="flex items-center space-x-2">
                            <Checkbox
                              id={`spec-${spec}`}
                              data-testid={`spec-${spec.toLowerCase()}`}
                              checked={formData.specializations.includes(spec)}
                              onCheckedChange={() => toggleSpecialization(spec)}
                            />
                            <label htmlFor={`spec-${spec}`} className="text-sm cursor-pointer">
                              {spec}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="bio" className="text-[#2C3333]">Bio/Description *</Label>
                      <Textarea
                        id="bio"
                        data-testid="bio-textarea"
                        value={formData.bio}
                        onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                        className="mt-2 rounded-lg border-border/60 bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[120px]"
                        placeholder="Tell seekers about your approach, experience, and how you can help..."
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-[#2C3333] mb-3 block">Languages Spoken *</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="languages">
                        {commonLanguages.map(lang => (
                          <div key={lang} className="flex items-center space-x-2">
                            <Checkbox
                              id={`lang-${lang}`}
                              data-testid={`lang-${lang.toLowerCase()}`}
                              checked={formData.languages.includes(lang)}
                              onCheckedChange={() => toggleLanguage(lang)}
                            />
                            <label htmlFor={`lang-${lang}`} className="text-sm cursor-pointer">
                              {lang}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#2C3333] mb-3 block">Session Type *</Label>
                      <RadioGroup value={formData.session_type} onValueChange={(value) => setFormData(prev => ({ ...prev, session_type: value }))} data-testid="session-type">
                        <div className="flex items-center space-x-2 mb-2">
                          <RadioGroupItem value="In-person" id="in-person" data-testid="session-in-person" />
                          <Label htmlFor="in-person" className="cursor-pointer font-normal">In-person</Label>
                        </div>
                        <div className="flex items-center space-x-2 mb-2">
                          <RadioGroupItem value="Online" id="online" data-testid="session-online" />
                          <Label htmlFor="online" className="cursor-pointer font-normal">Online</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Both" id="both" data-testid="session-both" />
                          <Label htmlFor="both" className="cursor-pointer font-normal">Both</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="flex gap-4">
                      <Button
                        type="submit"
                        data-testid="save-profile-btn"
                        className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full py-6 font-medium transition-all duration-300 shadow-sm hover:shadow-md"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {profile ? 'Save Changes' : 'Create Profile'}
                      </Button>
                      {profile && (
                        <Button
                          type="button"
                          data-testid="cancel-edit-btn"
                          onClick={() => {
                            setEditMode(false);
                            fetchProfile();
                          }}
                          variant="outline"
                          className="flex-1 rounded-full py-6"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderDashboard;